import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../../apiBase';
import { formatDate, getCategoryEmoji, getPlatformEmoji } from './bugHelpers';
import './BugReportsPage.css';

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
}

function StatCard({ icon, label, value, color }) {
    return (
        <div className="bug-stat-card">
            <div style={{ fontSize: 13, color: '#64748b' }}>{icon} {label}</div>
            <div className="value" style={{ color }}>{value}</div>
        </div>
    );
}

function BugDetailPanel({ bug, onClose, onUpdate, t }) {
    const [note, setNote] = useState(bug.adminNote || '');

    async function updateStatus(status) {
        await adminApi().put(`/admin/bugs/${bug.id}/status`, { status });
        onUpdate();
    }

    async function saveNote() {
        await adminApi().post(`/admin/bugs/${bug.id}/note`, { note });
        onUpdate();
    }

    async function adjustBonus(delta) {
        await adminApi().post(`/admin/bugs/${bug.id}/bonus`, { delta });
        onUpdate();
    }

    const screenshotSrc = bug.screenshotUrl?.startsWith('http')
        ? bug.screenshotUrl
        : bug.screenshotUrl;

    return (
        <div className="bug-detail-overlay" onClick={onClose}>
            <div className="bug-detail-panel" onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <h2 style={{ margin: 0, fontSize: 18 }}>
                        {getCategoryEmoji(bug.category)} #{bug.id}: {bug.title}
                    </h2>
                    <button type="button" onClick={onClose}>✕</button>
                </div>

                {bug.screenshotUrl && (
                    <section>
                        <img
                            src={screenshotSrc}
                            alt="Screenshot"
                            className="screenshot-img"
                        />
                        <a href={screenshotSrc} target="_blank" rel="noreferrer">
                            ⬇️ {t('detail.download')}
                        </a>
                    </section>
                )}

                <section>
                    <h3>{t('detail.description')}</h3>
                    <p>{bug.description || '—'}</p>
                </section>

                <section>
                    <h3>{t('detail.techInfo')}</h3>
                    <div className="info-grid">
                        <div><label>Платформа</label><span>{getPlatformEmoji(bug.platform)} {bug.platform}</span></div>
                        <div><label>Версия</label><span>{bug.appVersion}</span></div>
                        <div><label>Устройство</label><span>{bug.deviceInfo}</span></div>
                        <div><label>ОС</label><span>{bug.osVersion}</span></div>
                        <div><label>Экран</label><span>{bug.screen}</span></div>
                        <div><label>Дата</label><span>{formatDate(bug.createdAt)}</span></div>
                    </div>
                </section>

                <section>
                    <h3>{t('detail.testerInfo')}</h3>
                    <p>👤 {bug.testerName}<br />📧 {bug.testerEmail}<br />💎 +{bug.bonusPoints} pts</p>
                </section>

                <section>
                    <h3>{t('detail.management')}</h3>
                    <div className="status-buttons">
                        {['new', 'in_progress', 'fixed', 'wont_fix', 'duplicate'].map((s) => (
                            <button
                                key={s}
                                type="button"
                                className={`status-btn${bug.status === s ? ' active' : ''}`}
                                onClick={() => updateStatus(s)}
                            >
                                {t(`statuses.${s}`, { defaultValue: s })}
                            </button>
                        ))}
                    </div>

                    <div className="admin-note" style={{ marginTop: 16 }}>
                        <label>{t('detail.adminNote')}</label>
                        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
                        <button type="button" onClick={saveNote}>💾 {t('detail.saveNote')}</button>
                    </div>

                    <div style={{ marginTop: 12 }}>
                        <label>{t('detail.bonusPoints')}: {bug.bonusPoints}</label>
                        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                            <button type="button" onClick={() => adjustBonus(10)}>➕ +10</button>
                            <button type="button" onClick={() => adjustBonus(-10)}>➖ -10</button>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default function BugReportsPage() {
    const { t } = useTranslation('adminBugs');
    const [stats, setStats] = useState(null);
    const [bugs, setBugs] = useState([]);
    const [selected, setSelected] = useState(null);
    const [filters, setFilters] = useState({
        status: 'all',
        category: 'all',
        platform: 'all',
        priority: 'all',
        search: '',
    });

    const load = useCallback(async () => {
        const api = adminApi();
        const [statsRes, bugsRes] = await Promise.all([
            api.get('/admin/bugs/stats'),
            api.get('/admin/bugs', { params: filters }),
        ]);
        setStats(statsRes.data);
        setBugs(bugsRes.data);
        if (selected) {
            const fresh = bugsRes.data.find((b) => b.id === selected.id);
            if (fresh) setSelected(fresh);
        }
    }, [filters, selected]);

    useEffect(() => {
        load().catch(console.error);
    }, [load]);

    async function updateStatus(id, status, e) {
        e?.stopPropagation();
        await adminApi().put(`/admin/bugs/${id}/status`, { status });
        load();
    }

    return (
        <OwnerPageLayout title={t('title')} maxWidth={1400} className="bug-reports-page">
            <div className="stats-row">
                <StatCard icon="🔴" label={t('stats.critical')} value={stats?.critical ?? '—'} color="#b91c1c" />
                <StatCard icon="🟠" label={t('stats.new')} value={stats?.new ?? '—'} color="#c2410c" />
                <StatCard icon="🔵" label={t('stats.inProgress')} value={stats?.inProgress ?? '—'} color="#2563eb" />
                <StatCard icon="✅" label={t('stats.fixed')} value={stats?.fixedCount ?? '—'} color="#15803d" />
                <StatCard icon="👥" label={t('stats.testers')} value={stats?.testers ?? '—'} color="#7c3aed" />
            </div>

            <div className="bug-filters">
                <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                    <option value="all">Все статусы</option>
                    <option value="new">🆕 Новые</option>
                    <option value="in_progress">🔄 В работе</option>
                    <option value="fixed">✅ Исправлено</option>
                    <option value="wont_fix">🚫 Не будет</option>
                    <option value="duplicate">📋 Дубликат</option>
                </select>
                <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
                    <option value="all">Все типы</option>
                    <option value="crash">💥 Краш</option>
                    <option value="bug">🐛 Баг</option>
                    <option value="ui">🎨 UI</option>
                    <option value="translation">🌐 Перевод</option>
                    <option value="suggestion">💡 Идея</option>
                </select>
                <select value={filters.platform} onChange={(e) => setFilters({ ...filters, platform: e.target.value })}>
                    <option value="all">Все платформы</option>
                    <option value="android">🤖 Android</option>
                    <option value="ios">🍎 iOS</option>
                    <option value="web">🌐 Web</option>
                </select>
                <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
                    <option value="all">Все приоритеты</option>
                    <option value="critical">🔴 Критичный</option>
                    <option value="high">🟠 Высокий</option>
                    <option value="medium">🟡 Средний</option>
                    <option value="low">🟢 Низкий</option>
                </select>
                <input
                    placeholder="Поиск..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
            </div>

            <table className="bugs-table">
                <thead>
                    <tr>
                        <th>{t('table.id')}</th>
                        <th>{t('table.type')}</th>
                        <th>{t('table.title')}</th>
                        <th>{t('table.tester')}</th>
                        <th>{t('table.platform')}</th>
                        <th>{t('table.priority')}</th>
                        <th>{t('table.status')}</th>
                        <th>{t('table.date')}</th>
                        <th>{t('table.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {bugs.map((bug) => (
                        <tr key={bug.id} onClick={() => setSelected(bug)}>
                            <td>#{bug.id}</td>
                            <td>{getCategoryEmoji(bug.category)}</td>
                            <td>
                                <div className="bug-title">{bug.title}</div>
                                <div className="bug-screen">📍 {bug.screen}</div>
                            </td>
                            <td>
                                <div>{bug.testerName}</div>
                                <div className="bug-device">{bug.deviceInfo}</div>
                            </td>
                            <td>{getPlatformEmoji(bug.platform)}</td>
                            <td>
                                <span className={`badge-priority ${bug.priority}`}>{bug.priority}</span>
                            </td>
                            <td>
                                <select
                                    value={bug.status}
                                    onChange={(e) => updateStatus(bug.id, e.target.value, e)}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <option value="new">🆕</option>
                                    <option value="in_progress">🔄</option>
                                    <option value="fixed">✅</option>
                                    <option value="wont_fix">🚫</option>
                                    <option value="duplicate">📋</option>
                                </select>
                            </td>
                            <td>{formatDate(bug.createdAt)}</td>
                            <td>
                                <button type="button" onClick={(e) => { e.stopPropagation(); setSelected(bug); }}>👁️</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {selected && (
                <BugDetailPanel
                    bug={selected}
                    onClose={() => setSelected(null)}
                    onUpdate={load}
                    t={t}
                />
            )}
        </OwnerPageLayout>
    );
}
